/**
 * Client ของ `POST /v1/auth/login` — ตาม contract ที่ freeze ไว้ใน
 * `docs/API_CONTRACT.md` (D-045/D-046) และ implement ไว้แล้วที่
 * `apps/api/src/cap_erp_api/modules/rbac/auth_router.py`
 *
 * หลักการเดียวกับ client ตัวอื่นในโปรเจกต์: ฟังก์ชันนี้ **ไม่ throw** ความล้มเหลว
 * ทุกแบบกลายเป็นสถานะที่ผู้เรียกต้องแจกแจง จะได้ไม่มีใคร `catch` แล้วเดาว่าสำเร็จ
 *
 * สองข้อที่ต่างจาก client อื่นและตั้งใจให้ต่าง:
 *
 * 1. **ไม่ผ่าน `authorizedFetch`** — ยังไม่มี token ให้แนบ และไม่ควรให้ `401` ของ
 *    การ login ที่ล้มเหลวไปสั่งล้าง session ผ่าน trap ตัวนั้น การเข้าสู่ระบบที่
 *    ล้มเหลวไม่ใช่การถูก revoke
 * 2. **ทุกความล้มเหลวของ credential เป็นสถานะเดียว** (`rejected`) เพราะเซิร์ฟเวอร์
 *    ตอบ `401` ที่เหมือนกันทุกประการสำหรับ username ที่ไม่มี, รหัสผิด, credential
 *    ที่ถูก revoke, บัญชีที่ถูกล็อก และพนักงานที่ INACTIVE — ตั้งใจให้แยกไม่ออก
 *    ถ้าที่นี่แตกมันออกเป็นหลายสถานะ หน้าจอจะกลายเป็น oracle ที่ API ปฏิเสธที่จะเป็น
 *
 * ⚠️ ห้ามใส่รหัสผ่านลงในค่าที่คืนกลับ ลง log หรือลง error ใด ๆ (`docs/SECURITY.md`)
 */

import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงที่ client เรียก — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client ใช้ขึ้นต้นด้วย `/v1` เสมอ (`POST /auth/login` →
 * `POST /v1/auth/login`) เหมือน `ME_PERMISSIONS_PATH` และ `PRODUCTS_PATH`
 */
export const LOGIN_PATH = '/v1/auth/login';

/**
 * `device_kind` เลือก**อายุ session** เท่านั้น (D-046: Desktop 12 ชม./idle 30 นาที,
 * handheld 16 ชม./ไม่มี idle timeout)
 *
 * **ไม่ใช่สิทธิ์และไม่ใช่ความลับ** — client อ้างค่านี้เองได้อยู่แล้ว หน้าจอจึงต้อง
 * ไม่แสดงมันเป็นตัวเลือกบทบาท และต้องไม่ให้ผู้ใช้เลือกเอง ค่าถูกกำหนดจาก shell ที่
 * กำลังใช้อยู่เท่านั้น
 */
export type DeviceKind = 'DESKTOP' | 'HANDHELD';

/** `details.rule` ที่ `403` ของ credential ซึ่งถูกบังคับให้เปลี่ยนรหัสตอบมา (D-048) */
export const PASSWORD_CHANGE_REQUIRED_RULE = 'PASSWORD_CHANGE_REQUIRED';

/**
 * คีย์ของ token ที่จำกัดขอบเขตซึ่ง `403` ใบนั้นแนบมาให้ (D-050)
 *
 * ชื่อไม่ใช่ `token` โดยเจตนาของฝั่งเซิร์ฟเวอร์ — client ที่คว้า `token` จากคำตอบนี้
 * แล้วเก็บเป็นเซสชัน คือการยกระดับ token ที่จำกัดขอบเขตแบบเงียบ ๆ ซึ่ง D-050
 * ปฏิเสธไว้ตรง ๆ ที่นี่จึงอ่านคีย์นี้แยกและส่งต่อเป็นคนละฟิลด์กับ `token`
 */
export const PASSWORD_CHANGE_TOKEN_KEY = 'password_change_token';

export interface LoginCredentials {
  readonly username: string;
  readonly password: string;
  readonly deviceKind: DeviceKind;
}

export type LoginResult =
  /**
   * `expiresAt` มาจาก contract และถูกส่งต่อให้ผู้เรียกครบ แต่ **ไม่ถูกเก็บลง
   * storage** ตาม D-049 ("เก็บ token และเพียงสิ่งเดียวนั้น") ตัวตัดสินว่าเซสชันยัง
   * ใช้ได้ไหมคือเซิร์ฟเวอร์ผ่าน `401` ไม่ใช่นาฬิกาของเครื่อง client
   */
  | { readonly status: 'ready'; readonly token: string; readonly expiresAt: string | null }
  /** `401` — ทุกสาเหตุรวมกันเป็นสถานะเดียวโดยเจตนา */
  | { readonly status: 'rejected' }
  /**
   * `403` + `details.rule = PASSWORD_CHANGE_REQUIRED` และ **ไม่มี `token`** (D-048)
   *
   * `passwordChangeToken` คือสิ่งที่ D-050 เพิ่มเข้ามาใน `details` และเป็นทางออก
   * เดียวของบัญชีที่เพิ่งถูกรีเซ็ต: ใช้ได้กับ `POST /v1/me/password` เท่านั้น
   * **ไม่ใช่เซสชัน** และห้ามเก็บลง storage — ผู้เรียกต้องถือไว้ใน state เท่านั้น
   *
   * เป็น `null` ได้เมื่อเซิร์ฟเวอร์ตัวนั้นยังเป็นรุ่นก่อน D-050 (คำตอบเดิมที่มีแต่
   * `rule`) — สถานะนี้จึงยังเกิดได้โดยไม่มี token และหน้าจอต้องพูดตามจริง ไม่ใช่
   * เรนเดอร์ฟอร์มที่ไม่มีอะไรไปยื่นให้เซิร์ฟเวอร์
   */
  | { readonly status: 'password-change-required'; readonly passwordChangeToken: string | null }
  /** `403` อื่น ๆ ที่ contract ยังไม่ได้นิยาม — ไม่เดาว่าแปลว่าอะไร */
  | { readonly status: 'forbidden' }
  | { readonly status: 'bad-request' }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' };

export interface LoginRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ */
  readonly fetchImpl?: typeof fetch;
}

/**
 * body ที่ส่งออกไป — **เฉพาะสามคีย์นี้เท่านั้น**
 *
 * ประกอบจากฟิลด์ที่ระบุชื่อทีละตัว ไม่ใช่ spread ของ object ที่รับเข้ามา เพื่อให้
 * state ของฟอร์มที่อาจมีอะไรติดมาไม่มีทางหลุดไปกับคำขอ login
 */
export function serializeLoginBody(credentials: LoginCredentials): Record<string, unknown> {
  return {
    username: credentials.username,
    password: credentials.password,
    device_kind: credentials.deviceKind,
  };
}

/** อ่าน `details` จาก error envelope โดยไม่เชื่อรูปร่างของ body ที่ได้มา */
function detailsOf(payload: unknown): Record<string, unknown> {
  if (typeof payload !== 'object' || payload === null) {
    return {};
  }
  const details = (payload as Record<string, unknown>).details;
  if (typeof details !== 'object' || details === null) {
    return {};
  }
  return details as Record<string, unknown>;
}

/** ค่าสตริงที่ไม่ว่างของคีย์นั้นใน `details` หรือ `null` ถ้าไม่มี/ผิดชนิด */
function detailString(details: Record<string, unknown>, key: string): string | null {
  const value = details[key];
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    // body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return null;
  }
}

/**
 * แลก username/password เป็น session token
 *
 * รหัสผ่านอยู่ในพารามิเตอร์และใน body ที่ส่งออกเท่านั้น ไม่ถูกเก็บต่อ ไม่ถูกคืนกลับ
 * และไม่ปรากฏในสถานะความล้มเหลวใด ๆ ที่ฟังก์ชันนี้คืน
 */
export async function login(
  credentials: LoginCredentials,
  options: LoginRequestOptions = {},
): Promise<LoginResult> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, LOGIN_PATH);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeLoginBody(credentials)),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // ข้อความของ `TypeError` จาก fetch ไม่มีเนื้อ body อยู่ในนั้น รหัสผ่านจึงไม่รั่ว
    // ออกทางนี้ แต่ถึงอย่างนั้นก็ส่งต่อเฉพาะ `message` ไม่ใช่ทั้ง error object
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (response.status === 401) {
    return { status: 'rejected' };
  }

  if (response.status === 403) {
    const details = detailsOf(await readJson(response));
    if (detailString(details, 'rule') !== PASSWORD_CHANGE_REQUIRED_RULE) {
      return { status: 'forbidden' };
    }
    return {
      status: 'password-change-required',
      passwordChangeToken: detailString(details, PASSWORD_CHANGE_TOKEN_KEY),
    };
  }

  if (response.status === 400) {
    return { status: 'bad-request' };
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  const payload = await readJson(response);
  if (typeof payload !== 'object' || payload === null) {
    return { status: 'invalid-response' };
  }

  const record = payload as Record<string, unknown>;
  const token = typeof record.token === 'string' ? record.token.trim() : '';
  if (token === '') {
    // `200` ที่ไม่มี token ใช้ไม่ได้ — ถือเป็น payload ผิดรูป ไม่ใช่ "เข้าสู่ระบบสำเร็จ
    // แบบไม่มี token" ซึ่งจะพาไปหน้าที่โดน 401 ทุกคำขอ
    return { status: 'invalid-response' };
  }

  const expiresAt = typeof record.expires_at === 'string' ? record.expires_at : null;
  return { status: 'ready', token, expiresAt };
}
